import { tagEvents } from '@texas/components/tags/events';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useToast } from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { compositionVersionEvents } from '../events';
import { shipmentEvents } from '@texas/components/shared/shipment/events';
import { sampleEvents } from '@texas/components/shared/samples/events';
import { transportRequestEvents } from '@texas/components/shared/transportRequests/events';
import { useVersionOverviewContext } from './VersionOverviewContext';

interface Props {
  variantId: number;
  isMatrix: boolean;
}

interface CompositionLocationData {
  variantId: number;
  branchId?: number;
  supplierId?: number;
  cellX?: number;
  cellY?: number;
}

export function useVersionOverview({ variantId, isMatrix }: Props) {
  const {
    refetch,
    data: versions,
    loading: versionsLoading,
  } = useApiResource(compositionGroupApi.versionsOverview);
  const toast = useToast();
  const { t } = useTranslation();
  const { filters } = useVersionOverviewContext();

  const refetchVersions = useCallback(() => {
    refetch({
      variantId,
      ...filters,
    });
  }, [filters, refetch, variantId]);

  useEffect(() => {
    refetchVersions();
  }, [refetchVersions]);

  const [isCreating, setIsCreating] = useState(false);

  useSignalSubscriptionEvents({
    triggerOnEvent: refetchVersions,
    signalEvents: [
      tagEvents.tagUpdated,
      productApprovalEvents.productApprovalsCreated,
      productApprovalEvents.productApprovalRejected,
      compositionGroupEvents.onArchived,
      compositionGroupEvents.onRestored,
      compositionGroupEvents.onCreate,
      compositionGroupEvents.onCopy,
      compositionEvents.fileUpdated,
      compositionEvents.onRename,
      compositionGroupEvents.onRename,
      compositionEvents.onMaterialAdded,
      compositionEvents.onMaterialChanged,
      compositionEvents.onAdd,
      compositionEvents.onArchived,
      shipmentEvents.onShipmentUnpacked,
      compositionVersionEvents.onVersionsPatched,
      sampleEvents.onSampleReceivedFromSupplier,
      transportRequestEvents.onSamplesAdded,
      transportRequestEvents.onSamplesRemoved,
      sampleEvents.onSamplesLost,
      sampleEvents.onSampleSentFromSupplier,
      sampleEvents.onSamplesUpdated,
    ],
  });

  useEffect(
    () =>
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (
          event.entityType === EntityType.Supplier &&
          versions?.some((version) => version.supplierId === event.entityId)
        )
          refetchVersions();
      }),
    [refetchVersions, versions],
  );

  const createGroupRequest = async (basedOnId: number | undefined) => {
    setIsCreating(true);
    await request(
      compositionGroupApi.addGroup,
      [
        {
          variantId,
          basedOnId,
        },
      ],
      (_) => {
        compositionGroupEvents.onCreate.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
    setIsCreating(false);
  };
  const copyGroup = async (props: {
    fromId: number;
    location: CompositionLocationData;
    basedOnId?: number;
    replaceId?: number;
  }) => {
    setIsCreating(true);
    await request(
      compositionGroupApi.copyGroup,
      [
        props.fromId,
        {
          ...props.location,
          replaceCompositionGroupId: props.replaceId,
          basedOnId: props.basedOnId,
        },
      ],
      (_) => {
        compositionGroupEvents.onCopy.dispatch();
        toast({
          title: props.replaceId ? t('general.replaced') : t('general.copied'),
          status: 'success',
          isClosable: true,
        });
      },
      (error: ServerError) => {
        toast({
          title: t('general.copyFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
    setIsCreating(false);
  };
  const setBasedOnRequest = async (id: number, basedOnId: number) => {
    await request(
      compositionGroupApi.setBasedOn,
      [id, basedOnId],
      (_) => {
        refetchVersions();
      },
      (error: ServerError) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };
  const archiveGroupRequest = async (id: number) => {
    await request(
      compositionGroupApi.archive,
      [id],
      (_) => {
        compositionGroupEvents.onArchived.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.archiveFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };
  const restoreGroupRequest = async (id: number) => {
    await request(
      compositionGroupApi.restore,
      [id],
      (_) => {
        compositionGroupEvents.onRestored.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.restoreFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  const mainProductApproval = (id: number) => {
    return versions?.find((v) => v.id === id)?.mainProductApproval ?? undefined;
  };

  return {
    versions,
    versionsLoading,
    createGroupRequest,
    copyGroup,
    mainProductApproval,
    setBasedOnRequest,
    archiveGroupRequest,
    restoreGroupRequest,
    refetchVersions,
    isCreating,
    isMatrix,
  };
}
